import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useT } from '../../i18n';
import {
  checkForNewBuild,
  newBuildAvailable,
  newBuildReason,
  subscribeNewBuild,
} from '../../lib/api/newBuildAvailable';

/** How often to ask, while the tab is being looked at. */
const POLL_MS = 5 * 60 * 1000;

/**
 * The banner's height while it shows, on `<html>` — absent otherwise.
 *
 * IT SITS AT THE BOTTOM (owner, 2026-10-20), where other things already are:
 * the end of every page, the standalone player app's tab bar, the toast and
 * the floating dev/bug button. Each of those reads this to stand above the
 * banner instead of under it, so nothing it covers becomes unreachable. A
 * measured height rather than a guessed one, because the text wraps to two
 * lines on a phone and the safe-area inset adds to it.
 */
export const NEW_BUILD_BANNER_HEIGHT_VAR = '--new-build-banner-h';

// ─────────────────────────────────────────────────────────────────────
// «EN NY VERSJON ER TILGJENGELIG» (#537).
//
// IT OFFERS, IT NEVER ACTS. Reloading is what loses work in progress — the
// whole of #537 — so doing it automatically would inflict the harm this is
// meant to prevent, on somebody who did not choose it and could not have
// saved first. The button is the point: the person picks the moment.
//
// CHECKED ON FOCUS, and slowly otherwise. Coming back to the tab is exactly
// when somebody is about to act on what it says, and it costs one request
// for a file of forty bytes. A tab in the background is not being read, so
// polling it fast would buy nothing.
// ─────────────────────────────────────────────────────────────────────
export function NewBuildBanner({ check = checkForNewBuild }: { check?: typeof checkForNewBuild }) {
  const t = useT();
  const [stale, setStale] = useState(newBuildAvailable);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = subscribeNewBuild(setStale);
    const ask = () => void check();
    ask();
    const timer = setInterval(ask, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') ask();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      unsubscribe();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!stale || !el) return undefined;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(NEW_BUILD_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(NEW_BUILD_BANNER_HEIGHT_VAR);
    };
  }, [stale]);

  if (!stale) return null;

  return (
    <div
      ref={ref}
      data-testid="new-build-banner"
      role="status"
      className="fixed inset-x-0 bottom-0 z-30 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 pt-2.5 text-sm text-center"
      style={{
        background: '#1e3a8a',
        color: '#dbeafe',
        // The phone's home indicator is not somewhere to put a button.
        paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))',
        boxShadow: '0 -6px 20px -8px rgba(15, 22, 17, 0.35)',
      }}
    >
      <span>
        {t(newBuildReason() === 'refused_read' ? 'newBuild.refusedRead' : 'newBuild.available')}
      </span>
      {/* SOLID, NOT GHOST. `btn-ghost` is ink-grey text on nothing, which on
          this blue read as no button at all — and the button is the point. */}
      <button
        type="button"
        className="btn-lime"
        data-testid="new-build-reload"
        onClick={() => window.location.reload()}
      >
        {t('newBuild.reload')}
      </button>
    </div>
  );
}
